import { useLanguageStore } from '@/helpers/composables/useStores';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed } from 'vue';
import adminConfig from '@/features/promotions/config.json';

/*
US EOM Steering hierarchy
First
- ATV+ titles get ATV+
- CR-similar titles get CR (~50%)
	CR-similar + Hulu ==> CR
	CR-similar + Prime ==> CR
----
Then
- Hulu titles get Hulu FT
- Prime titles get Prime FT
----
Finally
- Hulu (25.5% inventory)
	Drama, Comedy, Crime, Reality TV, Romance, Music & Musical
- Prime (23% inventory)
	...rest >> History, Documentary, Sport, Western, War & Military, Made In Europe, + no genre
*/

const CRUNCHY_SUBGENRES = [
	'jpan',
	'ghbl',
	'faan',
	'acma',
	'avgm',
	'acsm',
	'adva',
	'faef',
	'fadf',
	'famf',
	'scsw',
	'sccy',
	'scif',
	'dyad',
	'drrd',
	'drpd',
	'sprd',
	'coro',
	'coac',
	'sitc',
	'psch',
	'hosl',
	'hopa',
	'myst',
	'acat',
	'sciz',
	'syad',
	'entw',
	'detc',
	'cssa',
	'drmy',
];

const CRUNCHY_TAGS = [
	// Core Anime
	'anime',
	'animation',
	'japan',
	'japanese',
	'tokyo',
	'manga',
	'based_on_comics',

	// Supernatural & Powers
	'supernatural',
	'supernatural_ability',
	'superhuman_powers',
	'magical',
	'wizards_and_magicians',
	'witch',
	'vampire',
	'werewolf',
	'zombie',
	'ghost',
	'demon',
	'angel',
	'monster',
	'creature',
	'immortality',
	'reincarnation',
	'curse',
	'prophecy',

	// Coming of Age
	'school',
	'high_school',
	'high_school_life',
	'college',
	'university',
	'student',
	'student_life',
	'teenage_life',
	'adolescence',
	'coming_of_age',
	'first_love',
	'young_lovers',
	'young_people',
	'prom',
	'graduation',
	'campus',

	// Space & Sci-Fi
	'space_and_aliens',
	'space_travel',
	'spacecraft',
	'alien_encounter',
	'dystopia',
	'cyberspace',
	'virtual_reality',
	'ai',
	'technology',

	// Fantasy Elements
	'dragon',
	'magic',
	'fairy',
	'fantasy_world',
	'medieval',
	'mythology',
	'legend',

	// Martial Arts
	'martial_arts',
	'sword_fight',
	'swords_and_armor',
	'samurai',
	'warrior',
	'fighting',
	'duel',
	'tournament',
	'championship',
	'contests_and_competitions',
	'battle_zone',
	'training',
];
//useTitleBasedSteering
export function useTitleBasedSteering({ title }: { title: MaybeRefOrGetter<TitleDetail> }) {
	const { country } = useLanguageStore();
	const _title = computed(() => toValue(title));
	// turn Title based steering on/off from config
	const enabled = adminConfig?.steering_overrides_us?.active ?? false;

	const isCrunchyrollTitle = computed(() => _title.value.offers.some(offer => offer.package.shortName === 'cru'));
	const isATVPlusTitle = computed(() => _title.value.offers.some(offer => offer.package.shortName === 'atp'));
	const isHuluTitle = computed(() => _title.value.offers.some(offer => offer.package.shortName === 'hlu'));
	const isPrimeTitle = computed(() => _title.value.offers.some(offer => offer.package.shortName === 'amp'));

	const hasCrunchyrollGenre = computed(() =>
		_title.value.content.genres.some(genre => ['ani', 'fnt', 'scf', 'trl'].includes(genre.shortName))
	);

	const hasCrunchyrollTag = computed(() =>
		(_title.value.content.tags ?? []).some(tag => CRUNCHY_TAGS.includes(tag.technicalName))
	);

	const hasCrunchyrollSubgenre = computed(() =>
		(_title.value.content.subgenres ?? []).some(subgenre =>
			CRUNCHY_SUBGENRES.includes(subgenre.content.shortName || '')
		)
	);

	const isCrunchyrollPromotion = computed(() => {
		if (country.value !== 'US' || isATVPlusTitle.value) return false;
		return (
			isCrunchyrollTitle.value ||
			hasCrunchyrollGenre.value ||
			hasCrunchyrollTag.value ||
			hasCrunchyrollSubgenre.value
		);
	});

	const hasHuluGenre = computed(() =>
		_title.value.content.genres.some(genre => ['drm', 'cmy', 'rly', 'msc', 'rma', 'crm'].includes(genre.shortName))
	);

	// If we pass null, usePromotion retures the config promotion
	const hardcodedShortName = computed(() => {
		if (country.value !== 'US' || !enabled) return null;

		if (isATVPlusTitle.value) return 'atp';
		if (isCrunchyrollTitle.value) return 'cru';
		if (isHuluTitle.value) return 'hlu';
		if (isPrimeTitle.value) return 'amp';

		if (isCrunchyrollPromotion.value) return 'cru';

		return hasHuluGenre.value ? 'hlu' : 'amp';
	});

	const hasTitleBasedSteering = computed(() => !!hardcodedShortName.value);

	return {
		isCrunchyrollPromotion,
		hardcodedShortName,
		hasTitleBasedSteering,
	};
}
